import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Chip,
  Avatar,
  Snackbar,
  Alert,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  AdminPanelSettings as AdminIcon,
  RestartAlt as ResetIcon,
} from '@mui/icons-material';
import { authAPI } from '../services/api';
import { MODULES, ROLE_DEFAULTS, effectivePermissions } from '../constants/permissions';

const roleColors = {
  reception: 'info',
  optometrist: 'primary',
  doctor: 'success',
  opd: 'secondary',
  admin: 'error',
};

const PermissionMatrix = () => {
  const [users, setUsers] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  const fetchUsers = async () => {
    try {
      const res = await authAPI.getUsers();
      setUsers(res.data.data);
    } catch (error) {
      showSnackbar('Failed to fetch users', 'error');
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const isChecked = (user, key) => effectivePermissions(user).includes(key);

  const handleToggle = async (user, key) => {
    const current = effectivePermissions(user);
    const next = current.includes(key)
      ? current.filter((k) => k !== key)
      : [...current, key];
    try {
      const res = await authAPI.updateUser(user._id, { permissions: next });
      setUsers((prev) => prev.map((u) => (u._id === user._id ? res.data.data : u)));
      showSnackbar(`${user.name}: ${MODULES.find((m) => m.key === key)?.label} ${next.includes(key) ? 'allowed' : 'revoked'}`);
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to update permission', 'error');
    }
  };

  const handleReset = async (user) => {
    try {
      const res = await authAPI.updateUser(user._id, { permissions: [] });
      setUsers((prev) => prev.map((u) => (u._id === user._id ? res.data.data : u)));
      showSnackbar(`${user.name}: reset to ${user.role} defaults`);
    } catch (error) {
      showSnackbar('Failed to reset permissions', 'error');
    }
  };

  return (
    <Box>
      <Paper variant="outlined" sx={{ mb: 2, p: 2, borderRadius: 2, boxShadow: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 40, height: 40, borderRadius: 1.5, bgcolor: 'rgba(124, 132, 212, 0.12)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'primary.main'
          }}>
            <AdminIcon />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight="700">Permission Matrix</Typography>
            <Typography variant="body2" color="text.secondary">
              Allow or revoke modules per user. Users without custom permissions inherit their role defaults.
            </Typography>
          </Box>
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'auto', boxShadow: 'none' }}>
        <TableContainer>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, minWidth: 200, bgcolor: 'background.paper', zIndex: 3, left: 0, position: 'sticky' }}>
                  User
                </TableCell>
                {MODULES.map((m) => (
                  <TableCell key={m.key} align="center" sx={{ fontWeight: 700, minWidth: 90, fontSize: '0.72rem', lineHeight: 1.2 }}>
                    {m.label}
                  </TableCell>
                ))}
                <TableCell align="right" sx={{ fontWeight: 700 }}>Reset</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.length === 0 ? (
                <TableRow><TableCell colSpan={MODULES.length + 2} align="center" sx={{ py: 5 }}>
                  <Typography color="text.secondary">No users found</Typography>
                </TableCell></TableRow>
              ) : users.map((user) => {
                const isAdmin = user.role === 'admin';
                const isCustom = !isAdmin && user.permissions && user.permissions.length > 0;
                return (
                  <TableRow key={user._id} hover>
                    <TableCell sx={{ position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.light', fontSize: '0.85rem' }}>
                          {user.name?.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>{user.name}</Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                            <Chip label={user.role} size="small" color={roleColors[user.role] || 'default'} sx={{ height: 18, fontSize: '0.65rem', textTransform: 'capitalize' }} />
                            {isCustom && (
                              <Typography variant="caption" color="warning.main" fontWeight={600}>custom</Typography>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    </TableCell>
                    {MODULES.map((m) => {
                      const roleDefault = ROLE_DEFAULTS[user.role]?.includes(m.key);
                      const isCore = m.key === 'dashboard';
                      const checked = isAdmin || isCore || isChecked(user, m.key);
                      return (
                        <TableCell key={m.key} align="center">
                          <Tooltip title={isAdmin ? 'Admin always has full access' : isCore ? 'Core module — always on' : roleDefault ? 'Role default' : 'Custom grant'}>
                            <span>
                              <Checkbox
                                size="small"
                                checked={checked}
                                disabled={isAdmin || isCore}
                                onChange={() => handleToggle(user, m.key)}
                                color={roleDefault || isAdmin ? 'primary' : 'warning'}
                              />
                            </span>
                          </Tooltip>
                        </TableCell>
                      );
                    })}
                    <TableCell align="right">
                      {!isAdmin && (
                        <Tooltip title="Reset to role defaults">
                          <IconButton size="small" onClick={() => handleReset(user)} disabled={!isCustom}>
                            <ResetIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
        Orange checkboxes mark permissions granted beyond the role default. Changes apply to the user's next login (or immediately if they refresh the page).
      </Typography>

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default PermissionMatrix;
